import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import OriginTracing from "@atlassiansox/origin-tracing";

import {
    trackContinueWithSocialSignups,
    isProduction,
    getIdentityBaseUrl,
    getPrimaryProductKeyFromBundle
} from "@/components/signup/utils";

import { useSignupFormContext } from "../../hooks";

import { LogoButton } from "./components";

export type Provider = "google" | "microsoft" | "apple" | "slack";
export type SignInProviders = Provider[];

type SignInButtonProps = {
    readonly hasRebrandedSignupFormStyles?: boolean;
    readonly customSignInProviders?: SignInProviders;
};

export const SignInButtons = ({
    hasRebrandedSignupFormStyles,
    customSignInProviders
}: SignInButtonProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { identityCobranding, redirectTo, edition, bundle } =
        useSignupFormContext();

    function getLoginOrigin(provider: string): string {
        const metadataId = `wac-signup-${provider}`;
        const originTracingOptions: { product: string; id?: string } = {
            product: metadataId
        };

        if (!isProduction()) {
            originTracingOptions.id = "static-uuid-for-testing";
        }

        const loginOrigin = new OriginTracing({ ...originTracingOptions });
        return loginOrigin.encode();
    }

    const providerLabels = {
        google: "Google",
        microsoft: "Microsoft",
        apple: "Apple",
        slack: "Slack"
    };

    const providers: SignInProviders =
        customSignInProviders ||
        (["google", "microsoft", "apple", "slack"] as const);

    const buttons = providers.map((provider) => {
        const continueUrl = new URL(redirectTo);

        continueUrl.searchParams.append("social-signup-login", "true");

        const params = new URLSearchParams({
            application: identityCobranding,
            continue: continueUrl.href,
            prompt: "select_account",
            atlOrigin: encodeURIComponent(getLoginOrigin(provider))
        });

        return {
            provider,
            label: providerLabels[provider],
            href: `${getIdentityBaseUrl()}/login/initiate/${provider}?${params}`
        };
    });

    return (
        <div css={buttons.length > 1 && buttonGridCss}>
            {buttons.map(({ provider, label, href }) => (
                <LogoButton
                    key={provider}
                    hasRebrandedSignupFormStyles={hasRebrandedSignupFormStyles}
                    href={href}
                    onClick={() =>
                        trackContinueWithSocialSignups(
                            createAnalyticsEvent,
                            provider,
                            getPrimaryProductKeyFromBundle(bundle),
                            edition
                        )
                    }
                    type={provider}
                    label={label}
                />
            ))}
        </div>
    );
};

const buttonGridCss = css({
    display: "grid",
    gap: "16px",
    gridTemplateColumns: "repeat(2, 1fr)"
});
